import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, message, Row, Col } from "antd";
import axios from "axios";
import { baseURL } from "./api";

const { Option } = Select;

/* ───────────────────────── helpers */
const allowOnlyNumbers = (e) => {
  if (!/[0-9.]/.test(e.key)) e.preventDefault();
};

const getUnitsForItem = (item) => {
  // 1️⃣ Custom units
  if (item.customUnits?.length) {
    return item.customUnits.map((u) => `${u.qty}${u.unit}`);
  }

  // 2️⃣ Weight units
  const units = [];
  if (item.price1kg > 0) units.push("1kg");
  if (item.price500gm > 0) units.push("500gm");
  if (item.price250gm > 0) units.push("250gm");

  // 3️⃣ 🔥 FALLBACK — FORCE DEFAULT
  if (units.length === 0) {
    units.push("1kg");
  }

  return units;
};


const VendorProductPricing = () => {
  const [vendors, setVendors] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [menuItems, setMenuItems] = useState([]);

  // { itemId: { unit: { purchasePrice, salePrice } } }
  const [productPricing, setProductPricing] = useState({});

  // { itemId: { unit: { p1,p2,p3 } } }
  const [profilePrices, setProfilePrices] = useState({});

  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [role, setRole] = useState(null);

  /* ───────────────────────── load vendors */
  useEffect(() => {
    const load = async () => {
      const { data } = await axios.get(`${baseURL}/getVendor`);
      setVendors(data);

      const r = localStorage.getItem("role");
      setRole(r);

      if (r === "vendor") {
        setSelectedVendor(localStorage.getItem("vendorId"));
      }
    };
    load();
  }, []);

  /* ───────────────────────── load restaurants */
  useEffect(() => {
    if (!selectedVendor) return;

    const load = async () => {
      const { data } = await axios.get(`${baseURL}/getRestaurant`);
      setRestaurants(
        data.filter((r) =>
          role === "admin"
            ? r.default === true
            : r.vendorId === selectedVendor || r.default === true
        )
      );
    };
    load();
  }, [selectedVendor, role]);

  /* ───────────────────────── load menu + vendor pricing */
  useEffect(() => {
    if (!selectedVendor || !selectedRestaurant) return;

    const load = async () => {
      const res = await axios.get(`${baseURL}/getMenuItems`, {
  params: { vendorId: selectedVendor, restaurantId: selectedRestaurant },
});

// 🔐 FORCE ARRAY
const menu = Array.isArray(res.data)
  ? res.data
  : res.data?.data || res.data?.menuItems || [];

setMenuItems(menu);


      const { data } = await axios.get(`${baseURL}/getProductPricing`, {
        params: { vendorId: selectedVendor, restaurantId: selectedRestaurant },
      });

      const map = {};
      menu.forEach((item) => {
        map[item._id] = {};
        getUnitsForItem(item).forEach((unit) => {
          map[item._id][unit] = { purchasePrice: "", salePrice: "" };
        });
      });

      const pricingList = Array.isArray(data?.pricing) ? data.pricing : [];

pricingList.forEach((p) => {
  if (!map[p.menuItemId?._id]) return;
  map[p.menuItemId._id][p.unit] = {
    purchasePrice: p.purchasePrice,
    salePrice: p.salePrice,
  };
});


      setProductPricing(map);
    };
    load();
  }, [selectedVendor, selectedRestaurant]);

  /* ───────────────────────── load profile prices */
  useEffect(() => {
    if (!selectedRestaurant) return;

    const load = async () => {
      const { data } = await axios.get(`${baseURL}/getProfilePrices`, {
        params: { restaurantId: selectedRestaurant },
      });

      const map = {};
const list = Array.isArray(data) ? data : [];

list.forEach((item) => {
  map[item.itemId] = {};
  item.units?.forEach((u) => {
    map[item.itemId][u.unit] = {
      p1: u.profile1,
      p2: u.profile2,
      p3: u.profile3,
    };
  });
});

      setProfilePrices(map);
    };
    load();
  }, [selectedRestaurant]);

  /* ───────────────────────── profile select */
  const handleProfileChange = (profile) => {
    setSelectedProfile(profile);

    const updated = {};
    menuItems.forEach((item) => {
      updated[item._id] = {};
      getUnitsForItem(item).forEach((unit) => {
        updated[item._id][unit] = {
          purchasePrice:
            profilePrices[item._id]?.[unit]?.[profile] ?? "",
          salePrice:
            productPricing[item._id]?.[unit]?.salePrice ?? "",
        };
      });
    });
    setProductPricing(updated);
  };

  /* ───────────────────────── price change */
  const handlePriceChange = (itemId, unit, key, value) => {
    setProductPricing((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [unit]: {
          ...prev[itemId]?.[unit],
          [key]: value,
        },
      },
    }));
  };

  /* ───────────────────────── submit */
  const onFinish = async () => {
    const pricingData = [];

    Object.entries(productPricing).forEach(([itemId, units]) => {
      Object.entries(units).forEach(([unit, p]) => {
        pricingData.push({
          menuItemId: itemId,
          unit,
          vendorId: selectedVendor,
          restaurantId: selectedRestaurant,
          purchasePrice: Number(p.purchasePrice) || 0,
          salePrice: Number(p.salePrice) || 0,
        });
      });
    });

    await axios.post(`${baseURL}/addProductPricing`, {
      vendorId: selectedVendor,
      restaurantId: selectedRestaurant,
      pricingData,
    });

    message.success("Pricing saved successfully");
  };

  /* ───────────────────────── render */
  return (
    <Form layout="vertical" onFinish={onFinish}>
      {role !== "vendor" && (
        <Form.Item label="Select Distributor">
          <Select value={selectedVendor} onChange={setSelectedVendor}>
            {vendors.map((v) => (
              <Option key={v._id} value={v._id}>
                {v.vendorName}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {selectedVendor && (
        <Form.Item label="Select Brand">
          <Select value={selectedRestaurant} onChange={setSelectedRestaurant}>
            {restaurants.map((r) => (
              <Option key={r._id} value={r._id}>
                {r.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {role === "admin" && selectedRestaurant && (
        <Form.Item label="Select Profile">
          <Select value={selectedProfile} onChange={handleProfileChange}>
            <Option value="p1">Profile 1</Option>
            <Option value="p2">Profile 2</Option>
            <Option value="p3">Profile 3</Option>
          </Select>
        </Form.Item>
      )}

      {/* Header */}
      <Row gutter={12} style={{ fontWeight: "bold", marginBottom: 8 }}>
        <Col span={6}>Product</Col>
        <Col span={4}>Unit</Col>
        {role !== "vendor" && <Col span={4}>Purchase</Col>}
        <Col span={4}>Sale</Col>
      </Row>

      {menuItems.map((item) =>
        getUnitsForItem(item).map((unit) => (
          <Row key={`${item._id}-${unit}`} gutter={12} style={{ marginBottom: 6 }}>
            <Col span={6}>
              <Input disabled value={item.name} />
            </Col>
            <Col span={4}>
              <Input disabled value={unit} />
            </Col>

            {role !== "vendor" && (
              <Col span={4}>
                <Input
                  value={productPricing[item._id]?.[unit]?.purchasePrice || ""}
                  onKeyPress={allowOnlyNumbers}
                  onChange={(e) =>
                    handlePriceChange(
                      item._id,
                      unit,
                      "purchasePrice",
                      e.target.value
                    )
                  }
                />
              </Col>
            )}

            <Col span={4}>
              <Input
                value={productPricing[item._id]?.[unit]?.salePrice || ""}
                onKeyPress={allowOnlyNumbers}
                onChange={(e) =>
                  handlePriceChange(
                    item._id,
                    unit,
                    "salePrice",
                    e.target.value
                  )
                }
              />
            </Col>
          </Row>
        ))
      )}

      <Button type="primary" htmlType="submit">
        Save Pricing
      </Button>
    </Form>
  );
};

export default VendorProductPricing;
